import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { Card } from '../components/Card';
import { ErrorMessage } from '../components/ErrorMessage';
import { GeoStatusBadge } from '../components/GeoStatusBadge';
import { PageLoader } from '../components/Loader';
import { EmailBounceNotice } from '../ops/EmailBounceNotice';
import { EmailPanel } from '../ops/EmailPanel';
import { ConversationInsights } from '../ops/ConversationInsights';
import { useLeadStatusLabels } from '../ops/leadStatus';
import { addressLine, appointmentStatusLabels, fmtDateTime, outcomeLabels, personName } from '../ops/format';
import { LeadTimeline } from '../ops/LeadTimeline';
import { PrivacyPanel } from '../ops/PrivacyPanel';
import { ContractConfiguratorModal } from '../documents/ContractConfiguratorModal';
import { DocumentsArea } from '../documents/DocumentsArea';
import { LeadInquiries } from '../m365/LeadInquiries';
import { DealsPanel } from '../sales/DealsPanel';
import { LeadOpenItems } from '../ops/OpenItems';
import { LeadContactEdit } from './leads/LeadContactEdit';
import { LeadNotes } from './leads/LeadNotes';
import { SameNumberLeads } from '../ops/SameNumberLeads';
import { LeadObjectsPanel } from '../objects/LeadObjectsPanel';
import { OwnersPanel } from '../owners/OwnersPanel';
import { TicketPanel } from '../ops/TicketPanel';
import { useProject, useSupportOnly } from '../projects/context';
import { useProjectApi } from '../projects/useProjectApi';

// LeadDetailPage: alles zu einem Lead auf einer Seite – Kontakt, Zuständigkeit, Termine, Anrufe,
// Rückfragen & Aufgaben, E-Mails, Historie und (für Berechtigte) Datenschutz.
export function LeadDetailPage() {
  const { leadId = '' } = useParams();
  const { project, can } = useProject();
  const papi = useProjectApi();
  const statusLabels = useLeadStatusLabels();
  const supportOnly = useSupportOnly();
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const lead = useQuery({ queryKey: ['projects', project.id, 'lead', leadId], queryFn: ({ signal }) => papi.lead(leadId, signal) });
  const resp = useQuery({
    queryKey: ['projects', project.id, 'responsibilities', leadId],
    queryFn: ({ signal }) => papi.responsibilities(leadId, signal),
    enabled: lead.isSuccess,
  });
  const appts = useQuery({
    queryKey: ['projects', project.id, 'lead-appointments', leadId],
    queryFn: ({ signal }) => papi.leadAppointments(leadId, signal),
    enabled: lead.isSuccess && can('appointment.read'),
  });
  const same = useQuery({
    queryKey: ['projects', project.id, 'same-number', leadId],
    queryFn: ({ signal }) => papi.sameNumberLeads(leadId, signal),
    enabled: lead.isSuccess,
  });
  const calls = useQuery({
    queryKey: ['projects', project.id, 'lead-calls', leadId],
    queryFn: ({ signal }) => papi.leadCalls(leadId, signal),
    enabled: lead.isSuccess && can('call.read'),
  });
  if (lead.isPending) return <PageLoader label="Lead wird geladen …" />;
  if (lead.isError) return <ErrorMessage error={lead.error} onRetry={() => void lead.refetch()} />;
  const l = lead.data;
  const chain = resp.data?.chain;
  const fieldSales = resp.data?.responsibles.field_sales ?? [];
  const region = (
    <dl className="facts">
      <dt>Geo</dt>
      <dd>
        <GeoStatusBadge status={l.geo_status} />
      </dd>
      <dt>Landkreis</dt>
      <dd>{chain?.county ?? '–'}</dd>
      <dt>Gebiet</dt>
      <dd>
        {chain?.area ?? '–'}
        {chain?.area_source === 'MANUAL' && <span className="muted"> (manuell)</span>}
        {chain?.area_source === 'RULE' && <span className="muted"> (Regelgebiet, Vorrang vor Geografie)</span>}
      </dd>
      <dt>Außendienst</dt>
      <dd>{fieldSales.map((r) => `${r.first_name} ${r.last_name}`.trim() + (r.is_primary ? ' (primär)' : '')).join(', ') || '–'}</dd>
    </dl>
  );
  return (
    <div className="page lead-detail">
      <header className="page-header">
        <div>
          <p className="eyebrow">Lead · {statusLabels[l.status] ?? l.status}</p>
          <h1>{personName(l)}</h1>
          <p className="muted">{addressLine(l)}</p>
        </div>
        <div className="form-actions">
          {can('document.edit') && (
            <button type="button" className="btn btn-primary" onClick={() => setConfiguratorOpen(true)}>
              Auftrags-Konfigurator (Slider)
            </button>
          )}
          {can('call.start') && !supportOnly && (
            <Link className={can('document.edit') ? 'btn' : 'btn btn-primary'} to={`/p/${project.key}/akquise/${l.id}`}>
              In Akquise öffnen
            </Link>
          )}
        </div>
      </header>
      {configuratorOpen && <ContractConfiguratorModal leadId={l.id} onClose={() => setConfiguratorOpen(false)} />}
      <LeadOpenItems leadId={l.id} />
      <SameNumberLeads items={same.data?.items ?? []} />
      <OwnersPanel leadId={l.id} />
      <LeadObjectsPanel leadId={l.id} />
      <div className={supportOnly ? undefined : 'detail-grid'}>
        <section className="card">
          <h2>Kontakt</h2>
          <dl className="facts">
            <dt>Ansprechpartner</dt>
            <dd>{[l.first_name, l.last_name].filter(Boolean).join(' ') || '–'}</dd>
            <dt>Telefon</dt>
            <dd>{l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : '–'}</dd>
            <dt>E-Mail</dt>
            <dd>{l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : '–'}</dd>
            <dt>Nächster Kontakt</dt>
            <dd>{fmtDateTime(l.next_contact_at)}</dd>
          </dl>
          <EmailBounceNotice leadId={l.id} email={l.email} />
          <LeadContactEdit lead={l} />
          <LeadNotes leadId={l.id} notes={l.notes} />
        </section>
        {!supportOnly && <Card title="Region & Zuständigkeit">{region}</Card>}
      </div>
      {supportOnly && can('document.read') && <DocumentsArea leadId={l.id} />}
      <ConversationInsights leadId={l.id} />
      <div id="rueckfragen">
        <TicketPanel leadId={l.id} />
      </div>
      {!supportOnly && can('document.read') && <DocumentsArea leadId={l.id} />}
      <LeadInquiries leadId={l.id} />
      {can('deal.read') && <DealsPanel leadId={l.id} />}
      {supportOnly && (
        <Card title="Region & Zuständigkeit" collapsible>
          {region}
        </Card>
      )}
      <div className="detail-grid">
        {can('appointment.read') && (
          <Card title="Termine" collapsible={supportOnly}>
            {!appts.data?.items.length ? (
              <p className="muted">Keine Termine.</p>
            ) : (
              <ul className="plain-list">
                {appts.data.items.map((a) => (
                  <li key={a.id}>
                    <Link to={`/p/${project.key}/termine/${a.id}`}>{fmtDateTime(a.start_at)}</Link>{' '}
                    <span className="muted">
                      · {appointmentStatusLabels[a.status]}
                      {a.field_sales_name && ` · ${a.field_sales_name}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
        {can('call.read') && (
          <Card title="Anrufe" collapsible={supportOnly}>
            {!calls.data?.items.length ? (
              <p className="muted">Keine Anrufe.</p>
            ) : (
              <ul className="plain-list">
                {calls.data.items.map((c) => (
                  <li key={c.id}>
                    {fmtDateTime(c.created_at)} · {c.outcome ? outcomeLabels[c.outcome] : c.status}
                    {c.user_name && <span className="muted"> · {c.user_name}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>
      <EmailPanel leadId={l.id} email={l.email} />
      <Card title="Historie" collapsible={supportOnly}>
        <LeadTimeline leadId={l.id} />
      </Card>
      <PrivacyPanel leadId={l.id} />
    </div>
  );
}
